/**
 * 3-D aircraft viewer for the V5 workbench "Aircraft" mode.
 *
 * Renders VSP's EXACT geometry: the backend (`/geometry/aircraft/build`) pulls
 * OpenVSP's real m_Surface control nets (machine-precision, no EGADS/point-fit)
 * and tessellates them to a per-component glTF. This viewer parses that glTF's
 * embedded buffer directly into one `THREE.Mesh` per component (no GLTFLoader
 * dependency — we own the format), orbits via three's OrbitControls, and lets the
 * user toggle / click components.
 *
 * Axis convention (OpenVSP): X = streamwise · Y = spanwise · Z = up.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import { fetchAircraftModel, type GltfDoc } from "../../api/aircraft";

const PALETTE = ["#6b8fb5", "#b58a6b", "#8ab56b", "#b56b9f", "#6bb5a8", "#b5b56b"];

function b64ToBuf(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

/** Parse the worker's glTF (one embedded buffer; per-mesh POSITION + indices). */
function parseGltf(doc: GltfDoc): { name: string; geometry: THREE.BufferGeometry }[] {
  const uri = doc.buffers[0]!.uri;
  const buf = b64ToBuf(uri.slice(uri.indexOf(",") + 1));
  return doc.meshes.map((mesh, i) => {
    const prim = mesh.primitives[0]!;
    const pa = doc.accessors[prim.attributes.POSITION]!;
    const ia = doc.accessors[prim.indices]!;
    const pv = doc.bufferViews[pa.bufferView]!;
    const iv = doc.bufferViews[ia.bufferView]!;
    const positions = new Float32Array(buf, pv.byteOffset, pa.count * 3);
    const indices = new Uint32Array(buf, iv.byteOffset, ia.count);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    g.setIndex(new THREE.BufferAttribute(indices, 1));
    g.computeVertexNormals();
    return { name: mesh.name ?? `component_${i}`, geometry: g };
  });
}

/**
 * Aircraft-level up-direction cue from the tessellated vertices — the client-side
 * mirror of the backend segmenter's fin cue (cs_segment.decide_up_sign). Some fleet
 * models are authored z-DOWN (the airshow C172), and a pre-baked bundle carries no
 * orientation, so the viewer decides from geometry: among vertices in the AFT part
 * of the aircraft that sit near the lateral centerline (the fin lives there; wings
 * and htail tips are gated out), the fin makes the z-extent strongly one-sided
 * relative to the whole-aircraft z-mid. Ambiguous shapes (finless flying wings,
 * canted twin fins outside the centerline gate) fall back to +1 — VSP's native
 * z-up — which real fleet checks show is the right default for exactly those.
 */
function meshUpSign(parts: { geometry: THREE.BufferGeometry }[], box: THREE.Box3): 1 | -1 {
  const xLen = box.max.x - box.min.x;
  const yMid = (box.min.y + box.max.y) / 2;
  const yLen = box.max.y - box.min.y;
  const zMid = (box.min.z + box.max.z) / 2;
  const zLen = box.max.z - box.min.z;
  if (!(xLen > 0) || !(zLen > 0)) return 1;
  const xGate = box.min.x + 0.6 * xLen;
  const yGate = Math.max(0.08 * yLen, 1e-6);
  let zHi = -Infinity;
  let zLo = Infinity;
  let count = 0;
  for (const part of parts) {
    const pos = part.geometry.getAttribute("position");
    for (let i = 0; i < pos.count; i++) {
      if (pos.getX(i) < xGate || Math.abs(pos.getY(i) - yMid) > yGate) continue;
      const z = pos.getZ(i);
      if (z > zHi) zHi = z;
      if (z < zLo) zLo = z;
      count++;
    }
  }
  if (count < 50) return 1;
  const above = zHi - zMid;
  const below = zMid - zLo;
  // The fin should dominate clearly; near-symmetric aft bodies stay z-up.
  if (Math.max(above, below) - Math.min(above, below) < 0.1 * zLen) return 1;
  return above >= below ? 1 : -1;
}

export interface AircraftViewer3DProps {
  modelUrl: string | null;
  visible?: Record<string, boolean>;
  selected?: string | null;
  onSelect?: (name: string | null) => void;
  width?: number;
  height?: number;
  dark?: boolean;
}

export function AircraftViewer3D({
  modelUrl, visible, selected, onSelect, width, height, dark = true,
}: AircraftViewer3DProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const groupRef = useRef<THREE.Group | null>(null);
  const meshesRef = useRef<Map<string, THREE.Mesh>>(new Map());
  const rafRef = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const bg = dark ? 0x14171c : 0xf4f5f7;

  // --- one-time scene setup ---
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const w = width ?? mount.clientWidth ?? 640;
    const h = height ?? mount.clientHeight ?? 480;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.setClearColor(bg, 1);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.01, 5000);
    camera.up.set(0, 0, 1); // Z up (VSP convention)
    camera.position.set(-18, -22, 10);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const key = new THREE.DirectionalLight(0xffffff, 0.9);
    key.position.set(-1, -1, 2);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.4);
    fill.position.set(1, 1, -1);
    scene.add(fill);

    const group = new THREE.Group();
    scene.add(group);

    rendererRef.current = renderer; sceneRef.current = scene;
    cameraRef.current = camera; controlsRef.current = controls; groupRef.current = group;

    const loop = () => {
      controls.update();
      renderer.render(scene, camera);
      rafRef.current = requestAnimationFrame(loop);
    };
    loop();

    // click-to-select via raycast
    const ray = new THREE.Raycaster();
    const onClick = (ev: MouseEvent) => {
      if (!onSelect) return;
      const rect = renderer.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((ev.clientX - rect.left) / rect.width) * 2 - 1,
        -((ev.clientY - rect.top) / rect.height) * 2 + 1,
      );
      ray.setFromCamera(ndc, camera);
      const hit = ray.intersectObjects(group.children, false)[0];
      onSelect(hit ? (hit.object.name || null) : null);
    };
    renderer.domElement.addEventListener("click", onClick);

    const ro = new ResizeObserver(() => {
      const cw = mount.clientWidth, ch = mount.clientHeight;
      if (cw && ch) {
        renderer.setSize(cw, ch);
        camera.aspect = cw / ch; camera.updateProjectionMatrix();
      }
    });
    ro.observe(mount);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      renderer.domElement.removeEventListener("click", onClick);
      controls.dispose();
      meshesRef.current.forEach((m) => m.geometry.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (rendererRef.current) rendererRef.current.setClearColor(bg, 1);
  }, [bg]);

  // --- (re)load the model ---
  useEffect(() => {
    const group = groupRef.current;
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!group || !camera || !controls || !modelUrl) return;
    let cancelled = false;
    setError(null);

    fetchAircraftModel(modelUrl)
      .then((doc) => {
        if (cancelled) return;
        // clear old
        meshesRef.current.forEach((m) => { group.remove(m); m.geometry.dispose(); });
        meshesRef.current.clear();
        const box = new THREE.Box3();
        const parts = parseGltf(doc);
        parts.forEach((part, i) => {
          const mat = new THREE.MeshStandardMaterial({
            color: PALETTE[i % PALETTE.length], metalness: 0.1, roughness: 0.65,
            side: THREE.DoubleSide, flatShading: false,
          });
          const mesh = new THREE.Mesh(part.geometry, mat);
          mesh.name = part.name;
          group.add(mesh);
          meshesRef.current.set(part.name, mesh);
          part.geometry.computeBoundingBox();
          if (part.geometry.boundingBox) box.union(part.geometry.boundingBox);
        });
        // Right-way-up: a z-down model (fin below the aircraft z-mid) rolls the
        // whole group 180 degrees about x. Model frame is untouched — display only.
        group.rotation.x = meshUpSign(parts, box) === -1 ? Math.PI : 0;
        group.updateMatrixWorld(true);
        // fit camera to the DISPLAYED (possibly rolled, off-origin) model
        const worldBox = new THREE.Box3().setFromObject(group);
        const center = new THREE.Vector3();
        worldBox.getCenter(center);
        const size = worldBox.getSize(new THREE.Vector3()).length() || 10;
        controls.target.copy(center);
        const dir = new THREE.Vector3(-0.7, -0.9, 0.45).normalize();
        camera.position.copy(center.clone().add(dir.multiplyScalar(size * 1.1)));
        camera.near = size / 100; camera.far = size * 100;
        camera.updateProjectionMatrix();
        controls.update();
      })
      .catch((e) => { if (!cancelled) setError(String(e?.message ?? e)); });

    return () => { cancelled = true; };
  }, [modelUrl]);

  // --- visibility ---
  useEffect(() => {
    meshesRef.current.forEach((mesh, name) => {
      mesh.visible = visible ? visible[name] !== false : true;
    });
  }, [visible]);

  // --- selection highlight ---
  useEffect(() => {
    meshesRef.current.forEach((mesh, name) => {
      const mat = mesh.material as THREE.MeshStandardMaterial;
      mat.emissive = new THREE.Color(name === selected ? 0x335577 : 0x000000);
    });
  }, [selected]);

  const style = useMemo<CSSProperties>(
    () => ({ width: width ?? "100%", height: height ?? "100%", position: "relative" }),
    [width, height],
  );

  return (
    <div ref={mountRef} style={style}>
      {error && (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center",
          color: "var(--v5-ink)", font: "13px var(--v5-mono, monospace)", padding: 16, textAlign: "center" }}>
          {error}
        </div>
      )}
    </div>
  );
}
